import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, ShoppingBag } from "lucide-react";
import { waLink } from "@/lib/site";
import { useCart } from "@/lib/cart";

const links = [
  { to: "/collection", label: "Collection" },
  { to: "/decants", label: "Decants" },
  { to: "/solid-perfume", label: "Solid Perfume" },
  { to: "/story", label: "Story" },
  { to: "/journal", label: "Journal" },
  { to: "/customer-care", label: "Customer Care" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { count } = useCart();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#CFC2AC]/70 border-b border-[#2B2118]/[0.06]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group">
          <span className="font-display text-lg md:text-xl tracking-wide text-[#2B2118]">Bonheur Eternal</span>
          <span className="ml-2 text-[10px] tracking-[0.3em] text-[#8F6B3A]">B.E.</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.12em] uppercase transition-colors duration-300 ${
                  isActive ? "text-[#8F6B3A]" : "text-[#5A4C3C] hover:text-[#2B2118]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid="nav-cart-btn" aria-label="Cart" className="relative p-2 text-[#5A4C3C] hover:text-[#8F6B3A] transition-colors">
            <ShoppingBag size={19} strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-1 rounded-full bg-[#8F6B3A] text-[#FBF8F1] text-[10px] font-medium flex items-center justify-center">{count}</span>
            )}
          </Link>
          <a
            href={waLink("Hello Bonheur Eternal, I'd like to enquire about your fragrances.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            className="flex items-center gap-2 rounded-full border border-[#8F6B3A]/40 text-[#8F6B3A] px-4 py-2 text-xs tracking-[0.15em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300"
          >
            <MessageCircle size={14} strokeWidth={1.5} />
            <span className="hidden sm:inline">WhatsApp</span>
          </a>
          <button
            data-testid="nav-hamburger"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#2B2118] p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#CFC2AC]/95 backdrop-blur-xl border-t border-[#2B2118]/[0.06]"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-display text-2xl text-[#2B2118]"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
